import type { MessageInitShape, MessageShape } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useMutation,
  useQuery,
} from '@connectrpc/connect-query'
import { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import {
  getBidLimitMatrix,
  getBidLimitMatrixScreen,
  updateBidLimitMatrix,
  updateBlockMandatoryPolicy,
  updateDefaultWithdrawOffset,
  updateFeeAutoConsiderPolicy,
  updateMarketOrderPolicy,
} from '@ebalance/proto/bffuser/v1/bid_limit_service-BidLimitService_connectquery'
import {
  GetBidLimitMatrixScreenResponseSchema,
  UpdateBidLimitMatrixRequestSchema,
  UpdateBidLimitMatrixResponseSchema,
  UpdateBlockMandatoryPolicyRequestSchema,
  UpdateBlockMandatoryPolicyResponseSchema,
  UpdateDefaultWithdrawOffsetRequestSchema,
  UpdateDefaultWithdrawOffsetResponseSchema,
  UpdateFeeAutoConsiderPolicyRequestSchema,
  UpdateFeeAutoConsiderPolicyResponseSchema,
  UpdateMarketOrderPolicyRequestSchema,
  UpdateMarketOrderPolicyResponseSchema,
} from '@ebalance/proto/bffuser/v1/bid_limit_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'

/** GetBidLimitMatrixScreen query hook for the AdminBidLimits page. */
export function useGetBidLimitMatrixScreen(): UseQueryResult<
  MessageShape<typeof GetBidLimitMatrixScreenResponseSchema>,
  ConnectError
> {
  return useQuery(getBidLimitMatrixScreen, {})
}

/** UpdateBidLimitMatrix mutation hook。 onSuccess で対象 segment の
 * GetBidLimitMatrix cache を invalidate する (= per-tab refresh)。
 */
export function useUpdateBidLimitMatrix(): UseMutationResult<
  MessageShape<typeof UpdateBidLimitMatrixResponseSchema>,
  ConnectError,
  MessageInitShape<typeof UpdateBidLimitMatrixRequestSchema>
> {
  const queryClient = useQueryClient()
  return useMutation(updateBidLimitMatrix, {
    onSuccess: async (_resp, req) => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrixScreen,
          input: {},
          cardinality: 'finite',
        }),
      })
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrix,
          input: { bidSegment: req.bidSegment },
          cardinality: 'finite',
        }),
      })
    },
  })
}

/** UpdateMarketOrderPolicy mutation hook。 即時保存 + 対象 segment の
 * GetBidLimitMatrix cache を invalidate。 */
export function useUpdateMarketOrderPolicy(): UseMutationResult<
  MessageShape<typeof UpdateMarketOrderPolicyResponseSchema>,
  ConnectError,
  MessageInitShape<typeof UpdateMarketOrderPolicyRequestSchema>
> {
  const queryClient = useQueryClient()
  return useMutation(updateMarketOrderPolicy, {
    onSuccess: async (_resp, req) => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrixScreen,
          input: {},
          cardinality: 'finite',
        }),
      })
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrix,
          input: { bidSegment: req.bidSegment },
          cardinality: 'finite',
        }),
      })
    },
  })
}

/** UpdateBlockMandatoryPolicy mutation hook。 即時保存 + SPOT_BLOCK tab
 * の GetBidLimitMatrix cache を invalidate (= block_mandatory_policy は
 * SPOT_BLOCK response にのみ含まれる)。 */
export function useUpdateBlockMandatoryPolicy(): UseMutationResult<
  MessageShape<typeof UpdateBlockMandatoryPolicyResponseSchema>,
  ConnectError,
  MessageInitShape<typeof UpdateBlockMandatoryPolicyRequestSchema>
> {
  const queryClient = useQueryClient()
  return useMutation(updateBlockMandatoryPolicy, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrixScreen,
          input: {},
          cardinality: 'finite',
        }),
      })
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrix,
          input: { bidSegment: BidSegment.SPOT_BLOCK },
          cardinality: 'finite',
        }),
      })
    },
  })
}

/** UpdateFeeAutoConsiderPolicy mutation hook。 即時保存 + 対象 segment の
 * GetBidLimitMatrix cache を invalidate (SPOT_NORMAL / INTRADAY のみ、
 * MarketOrderPolicy と同型)。 */
export function useUpdateFeeAutoConsiderPolicy(): UseMutationResult<
  MessageShape<typeof UpdateFeeAutoConsiderPolicyResponseSchema>,
  ConnectError,
  MessageInitShape<typeof UpdateFeeAutoConsiderPolicyRequestSchema>
> {
  const queryClient = useQueryClient()
  return useMutation(updateFeeAutoConsiderPolicy, {
    onSuccess: async (_resp, req) => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrixScreen,
          input: {},
          cardinality: 'finite',
        }),
      })
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrix,
          input: { bidSegment: req.bidSegment },
          cardinality: 'finite',
        }),
      })
    },
  })
}

/** UpdateDefaultWithdrawOffset mutation hook (bidding/bid-limits.md §4,
 * AC-BDL-501/502/503)。 即時保存 + INTRADAY tab の GetBidLimitMatrix cache を
 * invalidate (= default_withdraw_offset_setting は INTRADAY response にのみ
 * 含まれる)。 */
export function useUpdateDefaultWithdrawOffset(): UseMutationResult<
  MessageShape<typeof UpdateDefaultWithdrawOffsetResponseSchema>,
  ConnectError,
  MessageInitShape<typeof UpdateDefaultWithdrawOffsetRequestSchema>
> {
  const queryClient = useQueryClient()
  return useMutation(updateDefaultWithdrawOffset, {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrixScreen,
          input: {},
          cardinality: 'finite',
        }),
      })
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getBidLimitMatrix,
          input: { bidSegment: BidSegment.INTRADAY },
          cardinality: 'finite',
        }),
      })
    },
  })
}
